import { CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Suspense, lazy, useEffect } from "react";
import { Container as ModalContainer } from "react-modal-promise";
import { QueryClientProvider } from "react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { OrganizationProtectedRoutes } from "./components/OrganizationProtectedRoute";
import { SnackbarProvider } from "./context/SnackbarContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { AppThemeProvider } from "./context/ThemeContext";
import { ProvideAuth, useAuth } from "./hooks/useAuth";
import queryClient from "./state/QueryStore";

import "./App.css";
import OrganizationSettingsEnvironment from "./components/OrganizationSettingsEnvironment";
import { NavigatorProvider } from "./context/NavigatorContext";
import { OrganizationProvider } from "./context/OrganizationContext";
import { PaywallProvider } from "./context/PaywallContext";
import { ActionDetailsLoader } from "./layouts/skeleton-loaders/ActionDetailsLoader";
import { AssetAgentDetailsLoader } from "./layouts/skeleton-loaders/AssetAgentDetailsLoader";
import { AssetDatasetDetailsLoader } from "./layouts/skeleton-loaders/AssetDatasetDetailsLoader";
import { AssetDetailsLoader } from "./layouts/skeleton-loaders/AssetDetailsLoader";
import { AssetListPageLoader } from "./layouts/skeleton-loaders/AssetListPageLoader";
import { AssetModelDetailsLoader } from "./layouts/skeleton-loaders/AssetModelDetailsLoader";
import { AssetPolicyDetailsLoader } from "./layouts/skeleton-loaders/AssetPolicyDetailsLoader";
import { AuditDetailsLoader } from "./layouts/skeleton-loaders/AuditDetailsLoader";
import { EvaluationRunLoader } from "./layouts/skeleton-loaders/EvaluationRunLoader";
import { EvidenceDetailsLoader } from "./layouts/skeleton-loaders/EvidenceDetailsLoader";
import { InterfaceListLoader } from "./layouts/skeleton-loaders/InterfaceListLoader";
import { LibraryDetailsLoader } from "./layouts/skeleton-loaders/LibraryDetailsLoader";
import { ListViewLoader } from "./layouts/skeleton-loaders/ListViewLoader";
import { MainAuthenticatedPageLoader } from "./layouts/skeleton-loaders/MainAuthenticatedPageLoader";
import { MetricDetailsLoader } from "./layouts/skeleton-loaders/MetricDetailsLoader";
import { OrganizationSettingsApiKeyLoader } from "./layouts/skeleton-loaders/OrganizationSettingsApiKeyLoader";
import { OrganizationSettingsBillingLoader } from "./layouts/skeleton-loaders/OrganizationSettingsBillingLoader";
import { OrganizationSettingsGeneralLoader } from "./layouts/skeleton-loaders/OrganizationSettingsGeneralLoader";
import { OrganizationSettingsLoader } from "./layouts/skeleton-loaders/OrganizationSettingsLoader";
import { OrganizationUsersPageLoader } from "./layouts/skeleton-loaders/OrganizationUsersPageLoader";
import { RegistryDetailsLoader } from "./layouts/skeleton-loaders/RegistryDetailsLoader";
import { ResourceDetailsLoader } from "./layouts/skeleton-loaders/ResourceDetailsLoader";
import { RiskScenarioLoader } from "./layouts/skeleton-loaders/RiskScenarioLoader";
import { RoleDetailsLoader } from "./layouts/skeleton-loaders/RoleDetailsLoader";
import { RunnableDetailsLoader } from "./layouts/skeleton-loaders/RunnableDetailsLoader";
import { TestCaseDetailsLoader } from "./layouts/skeleton-loaders/TestCaseDetailsLoader";
import { UseCaseDetailLoader } from "./layouts/skeleton-loaders/UseCaseDetailLoader";
import { UseCaseTemplateListLoader } from "./layouts/skeleton-loaders/UseCaseTemplateListLoader";
import { UseCaseTemplateLoader } from "./layouts/skeleton-loaders/UseCaseTemplateLoader";
import { UserAccessKeysLoader } from "./layouts/skeleton-loaders/UserAccessKeysLoader";
import { VendorDetailsLoader } from "./layouts/skeleton-loaders/VendorDetailsLoader";
import { WorkflowDetailsLoader } from "./layouts/skeleton-loaders/WorkflowDetailsLoader";
import { FrameworksDetailLoader } from "./organisms/skeleton-loaders/FrameworksDetailLoader";
import { FrameworksListLoader } from "./organisms/skeleton-loaders/FrameworksListLoader";
import {
  ImpactAssessmentDetailLoader,
  ImpactAssessmentReviewLoader,
} from "./organisms/skeleton-loaders/ImpactAssessmentDetailLoader";
import { OrganizationOverviewLoader } from "./organisms/skeleton-loaders/OrganizationOverviewLoader";
import { PluginLoader } from "./organisms/skeleton-loaders/PluginLoader";
import { QuestionnaireBuilderLoader } from "./organisms/skeleton-loaders/QuestionnaireBuilderLoader";
import { UserSettingsLoader } from "./organisms/skeleton-loaders/UserSettingsLoader";
import { Checkout } from "./pages/Checkout";
import DangerZonePage from "./pages/DangerZonePage";
import DeploymentListPage from "./pages/DeploymentListPage";
import JiraIntegrationPage from "./pages/JiraIntegrationPage";
import DefaultWizard from "./pages/wizard/DefaultWizard";
import QuestionnaireWizard from "./pages/wizard/QuestionnaireWizard";
import VendorWizard from "./pages/wizard/VendorWizard";
import { updateIntercomLoggedIn, updateIntercomLoggedOut } from "./services/IntercomService";

/** Page loading should be done lazy */

// Default header navigator
const PageNavigator = lazy(() => import("./components/layout/PageWithNavigatorLayout"));

// 404 Not found
const InvalidRoutePage = lazy(() => import("./pages/InvalidRoutePage"));

// Profile
const AccountSettingsPage = lazy(() => import("./pages/UserSettingsPage"));
const AccessKeysPage = lazy(() => import("./pages/profile/AccessKeysPage"));

// Auth
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AuthProtectedRoute = lazy(() => import("./components/AuthProtectedRoute"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const RegisterUserPage = lazy(() => import("./pages/RegisterUserPage"));

// Metrics
const MetricDetailPage = lazy(() => import("./pages/tests/MetricDetailPage"));
const MetricsListPage = lazy(() => import("./pages/tests/MetricsListPage"));

// Workflows
const WorkflowsListPage = lazy(() => import("./pages/WorkflowsListPage"));
const WorkflowPage = lazy(() => import("./pages/WorkflowDetailsPage"));
const AgentListPage = lazy(() => import("./pages/AgentListPage"));
const AgentDetailsPage = lazy(() => import("./pages/AgentDetailsPage"));
const RunnableListPage = lazy(() => import("./pages/RunnableListPage"));
const RunnableDetailsPage = lazy(() => import("./pages/RunnableDetailsPage"));

// Actions
const ActionPage = lazy(() => import("./pages/ActionPage"));
const ActionsListPage = lazy(() => import("./pages/ActionsListPage"));

// Assets
const AssetPage = lazy(() => import("./pages/assets/AssetPage"));
const AssetDetailsPage = lazy(() => import("./pages/assets/AssetDetailsPage"));
const AssetPolicyListPage = lazy(() => import("./pages/assets/AssetPolicyListPage"));
const AssetPolicyPage = lazy(() => import("./pages/assets/AssetPolicyPage"));
const AssetDocumentListPage = lazy(() => import("./pages/assets/AssetDocumentListPage"));
const AssetDocumentPage = lazy(() => import("./pages/assets/AssetDocumentPage"));
const AssetPolicyEditorPage = lazy(() => import("./pages/assets/AssetPolicyEditorPage"));
const AssetExtResourcesListPage = lazy(() => import("./pages/assets/AssetExtResourcesListPage"));
const AssetAgentListPage = lazy(() => import("./pages/assets/AssetAgentListPage"));
const AssetTemplatePage = lazy(() => import("./pages/admin/AdminTemplateEditor"));
const AssetModelListPage = lazy(() => import("./pages/assets/AssetModelListPage"));
const AssetDatasetListPage = lazy(() => import("./pages/assets/AssetDatasetListPage"));
const AssetModelDetailsPage = lazy(() => import("./pages/assets/AssetModelDetailsPage"));
const AssetDatasetDetailsPage = lazy(() => import("./pages/assets/AssetDatasetDetailsPage"));
const AssetAgentDetailsPage = lazy(() => import("./pages/assets/AssetAgentDetailsPage"));

// Tests
const TestCasePage = lazy(() => import("./pages/tests/TestCasePage"));
const TestCasesListPage = lazy(() => import("./pages/tests/TestCasesListPage"));
const EvaluationRunListPage = lazy(() => import("./pages/tests/EvaluationRunListPage"));
const EvaluationRunPage = lazy(() => import("./pages/tests/EvaluationRunPage"));
const AlgorithmsPage = lazy(() => import("./pages/AlgorithmsPage"));
const DatasetsPage = lazy(() => import("./pages/DatasetsPage"));

// Compliances
const FrameworksDetailPage = lazy(() => import("./pages/compliance/FrameworksDetailPage"));
const FrameworksListPage = lazy(() => import("./pages/compliance/FrameworksListPage"));
const UseCaseListPage = lazy(() => import("./pages/compliance/UseCaseListPage"));
const UseCaseDetailPage = lazy(() => import("./pages/compliance/UseCaseDetailPage"));
const LibraryListPage = lazy(() => import("./pages/compliance/LibraryListPage"));
const LibraryDetailPage = lazy(() => import("./pages/compliance/LibraryDetailPage"));
const EvidenceDetailPage = lazy(() => import("./pages/compliance/EvidenceDetailPage"));
const QuestionnairesDetailPage = lazy(() => import("./pages/compliance/QuestionnairesDetailPage"));
const ImpactAssessmentListPage = lazy(() => import("./pages/compliance/ImpactAssessmentListPage"));
const ImpactAssessmentDetailPage = lazy(
  () => import("./pages/compliance/ImpactAssessmentDetailPage")
);
const EvidenceTestListPage = lazy(() => import("./pages/compliance/EvidenceTestListPage"));
const ImpactAssessmentReviewPage = lazy(
  () => import("./pages/compliance/ImpactAssessmentReviewPage")
);
const ImpactAssessmentSectionReviewPage = lazy(
  () => import("./pages/compliance/ImpactAssessmentSectionReviewPage")
);
const RiskScenariosListPage = lazy(() => import("./pages/compliance/RiskScenariosListPage"));
const RiskScenarioDetailPage = lazy(() => import("./pages/compliance/RiskScenariosDetailPage"));
const VendorsListPage = lazy(() => import("./pages/compliance/VendorsListPage"));
const VendorsDetailPage = lazy(() => import("./pages/compliance/VendorsDetailPage"));
const ImpactAssessmentTypeDetailPage = lazy(
  () => import("./pages/compliance/ImpactAssessmentTypeDetailPage")
);
const ImpactAssessmentTypePreviewPage = lazy(
  () => import("./pages/compliance/ImpactAssessmentTypePreviewPage")
);
const UseCaseTemplateListPage = lazy(() => import("./pages/compliance/UseCaseTemplateListPage"));
const UseCaseTemplatePage = lazy(() => import("./pages/compliance/UseCaseTemplatePage"));
const TemplateBuilderPage = lazy(() => import("./pages/compliance/TemplateBuilderPage"));
const QuestionnaireBuilderPage = lazy(() => import("./pages/compliance/QuestionnaireBuilderPage"));
const FairoTraceListPage = lazy(() => import("./pages/compliance/FairoTraceListPage"));
const InterfaceListPage = lazy(() => import("./pages/compliance/InterfaceListPage"));

// Roles
const RoleDetailPage = lazy(() => import("./pages/roles/RoleDetailPage"));
const RolesListPage = lazy(() => import("./pages/roles/RolesListPage"));
const PolicyListPage = lazy(() => import("./pages/roles/PolicyListPage"));

// Resources
const ResourcePage = lazy(() => import("./pages/resources/ResourcePage"));

// Organization
const OrganizationPage = lazy(() => import("./pages/OrganizationPage"));
const VerticalOrgSettings = lazy(() => import("./components/VerticalOrgSettings"));
const GithubIntegrationPage = lazy(() => import("./pages/GithubIntegrationPage"));
const OrganizationOverview = lazy(
  () => import("./organisms/organization/overview/OrganizationOverview")
);
const OrganizationDashboardSelector = lazy(
  () => import("./organisms/organization/overview/DashboardSelector")
);
const UserDashboardSelector = lazy(
  () => import("./organisms/organization/users/UserDashboardSelector")
);
const OrganizationUsersView = lazy(() => import("./components/OrganizationUsersView"));
const OrganizationBillingInformation = lazy(() => import("./components/BillingInformation"));
const OrganizationSettingsGeneral = lazy(() => import("./components/OrganizationSettingsGeneral"));
const OrganizationBrandingSettings = lazy(
  () => import("./components/OrganizationBrandingSettings")
);
const OrganizationSettingsApiKey = lazy(() => import("./components/OrganizationSettingsApiKey"));
const OrganizationIntegrations = lazy(
  () => import("./organisms/organization/integrations/OrganizationIntegrations")
);
const OrganizationSettingsAuditLog = lazy(
  () => import("./components/OrganizationSettingsAuditLog")
);
const CollectionStoreList = lazy(() => import("./pages/CollectionStoreListPage"));

// Audit Logs
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));

// Version History
const VersionHistoryListPage = lazy(() => import("./pages/VersionHistoryListPage"));

// Demo
const DemoPage = lazy(() => import("./pages/Demo"));
const QuickLinksListPage = lazy(() => import("./pages/QuickLinksListPage"));
const SubscriptionsPage = lazy(() => import("./pages/SubscriptionsPage"));
const CustomFieldListPage = lazy(() => import("./pages/CustomFieldsPage"));
const PublicLinksPage = lazy(() => import("./pages/PublicLinksPage"));
const PublicQuestionnairesListPage = lazy(() => import("./pages/PublicQuestionnairesListPage"));
const StripeProductsPage = lazy(() => import("./pages/StripeProductsPage"));
const LayoutStructuresListPage = lazy(() => import("./pages/LayoutStructuresListPage"));
const InstructionsPage = lazy(() => import("./pages/InstructionsPage"));

// Audits
const AuditsListPage = lazy(() => import("./pages/audits/AuditsListPage"));
const AuditsDetailPage = lazy(() => import("./pages/audits/AuditsDetailPage"));
const ReportsListPage = lazy(() => import("./pages/audits/ReportsListPage"));

// Public
const PublicQuestionnairePage = lazy(() => import("./pages/PublicQuestionnairePage"));

// Onboard
const OnboardSwitchPage = lazy(() => import("./pages/OnboardSwitchPage"));

const QuestionnaireListPage = lazy(() => import("./pages/QuestionnaireListPage"));

function App() {
  return (
    <ProvideAuth>
      <AppRouter />
    </ProvideAuth>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const AppRouter = () => {
  const { tryGetUser, user: loggedInUser, organization } = useAuth();
  const isAdmin = loggedInUser?.permissions === "Admin" ? true : false;
  useEffect(() => {
    if (loggedInUser && organization) {
      updateIntercomLoggedIn(loggedInUser, organization);
    } else {
      updateIntercomLoggedOut();
    }
  }, [loggedInUser, organization]);

  useEffect(() => {
    // fetch basic info requried in every session
    // like user, organization
    tryGetUser();
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <AppThemeProvider>
          <SnackbarProvider>
            <SubscriptionProvider>
              <NavigatorProvider>
                <PaywallProvider>
                  <OrganizationProvider>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <CssBaseline />
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <AuthProtectedRoute>
                              <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                <OnboardSwitchPage />
                              </Suspense>
                            </AuthProtectedRoute>
                          }
                        >
                          <Route
                            path="demo-settings"
                            element={
                              <PageNavigator initialShowOnlyFirst={true} initialDisabled={true} />
                            }
                          >
                            <Route
                              path=""
                              element={
                                <Suspense fallback={<DemoPage />}>
                                  <DemoPage />
                                </Suspense>
                              }
                            />
                          </Route>
                          <Route
                            path="organization"
                            element={
                              <PageNavigator initialShowOnlyFirst={true} initialDisabled={true} />
                            }
                          >
                            <Route
                              path=""
                              element={
                                <Suspense fallback={<></>}>
                                  <OrganizationPage />
                                </Suspense>
                              }
                            >
                              <Route
                                index
                                element={
                                  <Suspense fallback={<OrganizationOverviewLoader />}>
                                    <OrganizationDashboardSelector />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="overview"
                                element={
                                  <Suspense fallback={<OrganizationOverviewLoader />}>
                                    <OrganizationDashboardSelector />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="users"
                                element={
                                  <Suspense fallback={<OrganizationUsersPageLoader />}>
                                    <UserDashboardSelector />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="integrations"
                                element={
                                  <Suspense fallback={<PluginLoader />}>
                                    <OrganizationIntegrations />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="knowledge-store"
                                element={
                                  <Suspense fallback={<ListViewLoader type="metric" />}>
                                    <CollectionStoreList />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="roles"
                                element={
                                  <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                    <PageNavigator initialShowEditing={false} />
                                  </Suspense>
                                }
                              >
                                <Route
                                  path=""
                                  element={
                                    <Suspense fallback={<ListViewLoader type="role" />}>
                                      <RolesListPage />
                                    </Suspense>
                                  }
                                />

                                <Route
                                  path=":roleNumber"
                                  element={
                                    <Suspense fallback={<RoleDetailsLoader />}>
                                      <RoleDetailPage />
                                    </Suspense>
                                  }
                                />
                              </Route>
                              <Route
                                path="policies"
                                element={
                                  <Suspense fallback={<ListViewLoader type="policy" />}>
                                    <PolicyListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="template-gallery"
                                element={
                                  <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                    <PageNavigator initialShowEditing={false} />
                                  </Suspense>
                                }
                              >
                                <Route
                                  path=""
                                  element={
                                    <Suspense fallback={<UseCaseTemplateListLoader />}>
                                      <UseCaseTemplateListPage />
                                    </Suspense>
                                  }
                                />

                                <Route
                                  path=":templateNumber"
                                  element={
                                    <Suspense fallback={<UseCaseTemplateLoader />}>
                                      <UseCaseTemplatePage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="builder"
                                  element={
                                    <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                      <TemplateBuilderPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="builder/:templateId"
                                  element={
                                    <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                      <TemplateBuilderPage />
                                    </Suspense>
                                  }
                                />
                              </Route>
                              {isAdmin ? (
                                <Route
                                  path="settings"
                                  element={
                                    <Suspense fallback={<OrganizationSettingsLoader />}>
                                      <VerticalOrgSettings />
                                    </Suspense>
                                  }
                                >
                                  <Route
                                    index
                                    element={
                                      <Suspense fallback={<OrganizationSettingsGeneralLoader />}>
                                        <OrganizationSettingsGeneral />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="general"
                                    element={
                                      <Suspense fallback={<OrganizationSettingsGeneralLoader />}>
                                        <OrganizationSettingsGeneral />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="brand"
                                    element={
                                      <Suspense fallback={<OrganizationSettingsGeneralLoader />}>
                                        <OrganizationBrandingSettings />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="environment"
                                    element={
                                      <Suspense fallback={<OrganizationSettingsGeneralLoader />}>
                                        <OrganizationSettingsEnvironment />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="api"
                                    element={
                                      <Suspense fallback={<OrganizationSettingsApiKeyLoader />}>
                                        <OrganizationSettingsApiKey />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="billing"
                                    element={
                                      <Suspense fallback={<OrganizationSettingsBillingLoader />}>
                                        <OrganizationBillingInformation />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="audit-log"
                                    element={
                                      <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                        <OrganizationSettingsAuditLog />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="quick-links"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <QuickLinksListPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="subscriptions"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <SubscriptionsPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="custom-fields"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <CustomFieldListPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="public-links"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <PublicLinksPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="public-questionnaires"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <PublicQuestionnairesListPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="stripe-products"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <StripeProductsPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="layout-structures"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <LayoutStructuresListPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="instructions"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <InstructionsPage />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="danger-zone"
                                    element={
                                      <Suspense fallback={<></>}>
                                        <DangerZonePage />
                                      </Suspense>
                                    }
                                  />
                                </Route>
                              ) : null}
                            </Route>
                          </Route>
                          <Route
                            path="organization/register_user"
                            element={
                              <Suspense>
                                <RegisterUserPage />
                              </Suspense>
                            }
                          />
                          <Route
                            element={
                              <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                <OrganizationProtectedRoutes />
                              </Suspense>
                            }
                          >
                            <Route
                              path="workflows"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="workflow" />}>
                                    <WorkflowsListPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="workflows/:clientWorkflowId"
                              element={
                                <Suspense fallback={<WorkflowDetailsLoader />}>
                                  <AgentDetailsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="runnables"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="runnable" />}>
                                    <RunnableListPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="runnables/:clientRunnableId"
                              element={
                                <Suspense fallback={<RunnableDetailsLoader />}>
                                  <RunnableDetailsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="tests"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path="evaluation-runs"
                                element={
                                  <Suspense fallback={<ListViewLoader type="evaluation-run" />}>
                                    <EvaluationRunListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="test-cases"
                                element={
                                  <Suspense fallback={<ListViewLoader type="testcase" />}>
                                    <TestCasesListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="metrics"
                                element={
                                  <Suspense fallback={<ListViewLoader type="metric" />}>
                                    <MetricsListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="evaluation-runs/:evaluationRunNumber"
                                element={
                                  <Suspense fallback={<EvaluationRunLoader />}>
                                    <EvaluationRunPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="test-cases/:testNumber"
                                element={
                                  <Suspense fallback={<TestCaseDetailsLoader />}>
                                    <TestCasePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="metrics/:metricNumber"
                                element={
                                  <Suspense fallback={<MetricDetailsLoader />}>
                                    <MetricDetailPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="oversight"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path="risk-scenarios"
                                element={
                                  <Suspense fallback={<ListViewLoader type="risk-scenario" />}>
                                    <RiskScenariosListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="evidence-tests"
                                element={
                                  <Suspense fallback={<ListViewLoader type="evidence-test" />}>
                                    <EvidenceTestListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="frameworks"
                                element={
                                  <Suspense fallback={<FrameworksListLoader />}>
                                    <FrameworksListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="use-cases"
                                element={
                                  <Suspense fallback={<ListViewLoader type="use-case" />}>
                                    <UseCaseListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="questionnaires"
                                element={
                                  <Suspense fallback={<ListViewLoader type="questionnaire" />}>
                                    <QuestionnaireListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="deployments"
                                element={
                                  <Suspense fallback={<ListViewLoader type="deployment" />}>
                                    <DeploymentListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="interfaces"
                                element={
                                  <Suspense fallback={<InterfaceListLoader />}>
                                    <InterfaceListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="library"
                                element={
                                  <Suspense fallback={<ListViewLoader type="library" />}>
                                    <LibraryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment"
                                element={
                                  <Suspense fallback={<ListViewLoader type="impact-assessment" />}>
                                    <ImpactAssessmentListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="vendors"
                                element={
                                  <Suspense fallback={<ListViewLoader type="vendor" />}>
                                    <VendorsListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="traces"
                                element={
                                  <Suspense fallback={<ListViewLoader type="traces" />}>
                                    <FairoTraceListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="questionnaires/:questionnaireNumber"
                                element={
                                  <Suspense fallback={<QuestionnaireBuilderLoader />}>
                                    <QuestionnaireBuilderPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="frameworks/:frameworkNumber"
                                element={
                                  <Suspense fallback={<FrameworksDetailLoader />}>
                                    <FrameworksDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="frameworks/fairo/:frameworkNumber"
                                element={
                                  <Suspense fallback={<FrameworksDetailLoader />}>
                                    <FrameworksDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="risk-scenarios/:riskNumber"
                                element={
                                  <Suspense fallback={<RiskScenarioLoader />}>
                                    <RiskScenarioDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="evidence-tests/:libraryNumber"
                                element={
                                  <Suspense fallback={<EvidenceDetailsLoader />}>
                                    <EvidenceDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="use-cases/:useCaseName"
                                element={
                                  <Suspense fallback={<UseCaseDetailLoader />}>
                                    <UseCaseDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="library/fairo/:libraryNumber"
                                element={
                                  <Suspense fallback={<LibraryDetailsLoader />}>
                                    <LibraryDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="library/:libraryNumber"
                                element={
                                  <Suspense fallback={<LibraryDetailsLoader />}>
                                    <LibraryDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="questionnaires/:questionnaireNumber"
                                element={
                                  <Suspense fallback={<RegistryDetailsLoader />}>
                                    <QuestionnairesDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment/:impactAssessmentNumber"
                                element={
                                  <Suspense fallback={<ImpactAssessmentDetailLoader />}>
                                    <ImpactAssessmentDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment/:impactAssessmentNumber/questionnaire/:questionnaireNumber/review"
                                element={
                                  <Suspense fallback={<ImpactAssessmentReviewLoader />}>
                                    <ImpactAssessmentReviewPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment/:impactAssessmentNumber/section/:sectionNumber/review"
                                element={
                                  <Suspense fallback={<ImpactAssessmentReviewLoader />}>
                                    <ImpactAssessmentSectionReviewPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="vendors/:vendorNumber"
                                element={
                                  <Suspense fallback={<VendorDetailsLoader />}>
                                    <VendorsDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment-type/:impactAssessmentTypeNumber"
                                element={
                                  <Suspense fallback={<ImpactAssessmentDetailLoader />}>
                                    <ImpactAssessmentTypeDetailPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="impact-assessment-type/:impactAssessmentTypeNumber/preview"
                                element={
                                  <Suspense fallback={<ImpactAssessmentDetailLoader />}>
                                    <ImpactAssessmentTypePreviewPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="algorithms"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <AlgorithmsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="datasets"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <DatasetsPage />
                                </Suspense>
                              }
                            />
                            <Route
                              path="actions"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="action" />}>
                                    <ActionsListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":actionNumber"
                                element={
                                  <Suspense fallback={<ActionDetailsLoader />}>
                                    <ActionPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="assets"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path="policies"
                                element={
                                  <Suspense fallback={<AssetListPageLoader type="policy" />}>
                                    <AssetPolicyListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="documentation"
                                element={
                                  <Suspense fallback={<AssetListPageLoader type="documentation" />}>
                                    <AssetDocumentListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="models"
                                element={
                                  <Suspense fallback={<ListViewLoader type="model" />}>
                                    <AssetModelListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="datasets"
                                element={
                                  <Suspense fallback={<ListViewLoader type="dataset" />}>
                                    <AssetDatasetListPage />c
                                  </Suspense>
                                }
                              />
                              <Route
                                path="external-resources"
                                element={
                                  <Suspense fallback={<ListViewLoader type="resource" />}>
                                    <AssetExtResourcesListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="agents"
                                element={
                                  <Suspense fallback={<ListViewLoader type="agent" />}>
                                    <AssetAgentListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="policies/:policyNumber"
                                element={
                                  <Suspense fallback={<AssetPolicyDetailsLoader />}>
                                    <AssetPolicyPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="documentation/:documentNumber"
                                element={
                                  <Suspense fallback={<AssetPolicyDetailsLoader />}>
                                    <AssetDocumentPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="external-resources/:resourceNumber"
                                element={
                                  <Suspense fallback={<ResourceDetailsLoader />}>
                                    <ResourcePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="policies/:policyNumber/policy-editor"
                                element={
                                  <Suspense fallback={<AssetDetailsLoader />}>
                                    <AssetPolicyEditorPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="policies/:policyNumber/template"
                                element={
                                  <Suspense fallback={<AssetDetailsLoader />}>
                                    <AssetTemplatePage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="models/:assetNumber"
                                element={
                                  <Suspense fallback={<AssetModelDetailsLoader />}>
                                    <AssetModelDetailsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="datasets/:assetNumber"
                                element={
                                  <Suspense fallback={<AssetDatasetDetailsLoader />}>
                                    <AssetDatasetDetailsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="agents/:assetNumber"
                                element={
                                  <Suspense fallback={<AssetAgentDetailsLoader />}>
                                    <AssetAgentDetailsPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="profile"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialDisabled={true} />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<UserSettingsLoader />}>
                                    <AccountSettingsPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="access-keys"
                                element={
                                  <Suspense fallback={<UserAccessKeysLoader />}>
                                    <AccessKeysPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="audit-log"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator
                                    initialShowFirstLast={true}
                                    initialShowEditing={false}
                                  />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2/:objectId2"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2/:objectId2/:objectType3"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2/:objectId2/:objectType3/:objectId3"
                                element={
                                  <Suspense fallback={<ListViewLoader type="auditlog" />}>
                                    <AuditLogPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="version-history"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator
                                    initialShowFirstLast={true}
                                    initialShowEditing={false}
                                  />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1"
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1"
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2"
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2/:objectId2"
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":objectType1/:objectId1/:objectType2/:objectId2/:objectType3/:objectId3"
                                element={
                                  <Suspense fallback={<ListViewLoader type="version" />}>
                                    <VersionHistoryListPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                            <Route
                              path="audits"
                              element={
                                <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                  <PageNavigator initialShowEditing={false} />
                                </Suspense>
                              }
                            >
                              <Route
                                path=""
                                element={
                                  <Suspense fallback={<ListViewLoader type="audit" />}>
                                    <AuditsListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="reports"
                                element={
                                  <Suspense fallback={<ListViewLoader type="report" />}>
                                    <ReportsListPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path=":auditNumber"
                                element={
                                  <Suspense fallback={<AuditDetailsLoader />}>
                                    <AuditsDetailPage />
                                  </Suspense>
                                }
                              />
                            </Route>
                          </Route>
                        </Route>
                        <Route
                          path="/oversight/use-cases/wizard/:useCaseName"
                          element={
                            <AuthProtectedRoute>
                              <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                <QuestionnaireWizard />
                              </Suspense>
                            </AuthProtectedRoute>
                          }
                        />
                        <Route
                          path="/oversight/vendors/wizard/:vendorName"
                          element={
                            <AuthProtectedRoute>
                              <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                <VendorWizard />
                              </Suspense>
                            </AuthProtectedRoute>
                          }
                        />
                        <Route
                          path="/oversight/questionnaires/wizard"
                          element={
                            <AuthProtectedRoute>
                              <Suspense fallback={<MainAuthenticatedPageLoader />}>
                                <DefaultWizard />
                              </Suspense>
                            </AuthProtectedRoute>
                          }
                        />
                        <Route path="*" element={<InvalidRoutePage />} />
                        <Route
                          path="/login"
                          element={
                            <Suspense>
                              <LoginPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <Suspense>
                              <RegisterPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/forgotpassword"
                          element={
                            <Suspense>
                              <ForgotPassword />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/subscribe/:status"
                          element={
                            <Suspense>
                              <Checkout />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/integrations/github"
                          element={
                            <Suspense>
                              <GithubIntegrationPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/integrations/jira"
                          element={
                            <Suspense>
                              <JiraIntegrationPage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/public/:slug/:objectId"
                          element={
                            <Suspense>
                              <PublicQuestionnairePage />
                            </Suspense>
                          }
                        />
                      </Routes>
                      <ModalContainer />
                    </LocalizationProvider>
                  </OrganizationProvider>
                </PaywallProvider>
              </NavigatorProvider>
            </SubscriptionProvider>
          </SnackbarProvider>
        </AppThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
